import { useAtomValue } from 'jotai';

import { Box, Divider, Group, Stack, Text } from '@ekhodealer/ekho-common/rev';

import type { LeadDetails } from '@ekhodealer/ekho-common';

import { useGetLeadDetails } from '@common/api/leads/useGetLeadDetails';
import { AccordionSection } from '@common/components/AccordionSection';
import { FicoScoreDisplay } from '@common/components/FicoScoreDisplay';
import { InfoLineItem } from '@common/components/InfoLineItem';

import { leadDetailsDrawerAtom } from '../../atoms/leadDetailsDrawerAtoms';
import { PrequalCell } from '../table-cells/PrequalCell';

import { LeadsVehicle } from './shared/LeadsVehicle';

const SECTION_TITLE = 'Pre-qualifications';

interface PreQualificationItemProps {
	prequalification: LeadDetails['prequalifications'][number];
}

/**
 * Individual prequalification item display component
 * Shows date filed, status, credit details, and optional vehicle
 */
const PreQualificationItem = ({ prequalification }: PreQualificationItemProps) => {
	const { dateFiled, status, creditDetails, vehicle } = prequalification;
	return (
		<Box rounding='12' background='secondary' padding='16'>
			<Stack itemsSpacing='12'>
				{/* Date Filed and Status Group */}
				<Group itemsSpacing='12' noWrap itemsAlignX='stretch'>
					<Box rounding='12' padding='12' background='primary'>
						<Stack>
							<Text size='footnote'>Date filed</Text>
							<Text size='bodySmall' weight='semibold'>
								{dateFiled}
							</Text>
						</Stack>
					</Box>
					<Box rounding='12' padding='12' background='primary'>
						<Stack>
							<Text size='footnote'>Status</Text>
							<PrequalCell prequalStatus={status} />
						</Stack>
					</Box>
				</Group>

				{/* Credit Details Box */}
				<Box rounding='12' padding='12' background='primary'>
					<Stack itemsSpacing='12'>
						<InfoLineItem
							title='FICO score'
							content={
								<FicoScoreDisplay
									ficoScore={creditDetails.ficoScore}
									ficoScoreRating={creditDetails.ficoRating}
								/>
							}
						/>
						<InfoLineItem
							title='Outstanding accounts'
							value={creditDetails.outstandingAccounts.toString()}
						/>
						<InfoLineItem
							title='Avg account age'
							value={`${creditDetails.averageAccountAge} years`}
						/>
						<InfoLineItem title='Credit summary' value={creditDetails.summary} />
					</Stack>
				</Box>

				{/* Vehicle (if exists) */}
				{vehicle && <LeadsVehicle vehicle={vehicle} />}
			</Stack>
		</Box>
	);
};

export const PreQualificationsSection = () => {
	const { selectedRow } = useAtomValue(leadDetailsDrawerAtom);
	const { data: leadDetails } = useGetLeadDetails(selectedRow?.id);

	if (!leadDetails || !selectedRow?.id) {
		return null;
	}

	const { prequalifications } = leadDetails;

	// Hide section if no prequalifications
	if (prequalifications.length === 0) {
		return null;
	}

	const sectionTitle = `${SECTION_TITLE} (${prequalifications.length})`;

	return (
		<>
			<Divider />
			<AccordionSection title={sectionTitle} defaultExpanded>
				<Stack itemsSpacing='12'>
					{prequalifications.map((prequalification, index) => (
						<PreQualificationItem key={index} prequalification={prequalification} />
					))}
				</Stack>
			</AccordionSection>
		</>
	);
};
